'use client';

import { useMemo } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceArea,
} from 'recharts';

export interface RecessionRange { start: string; end: string }

export interface ChartObservation {
  date: string;
  value: number | null;
  dateMs: number;
}

export interface PreparedSeries {
  observations: ChartObservation[];
  hasData: boolean;
  yMin: number;
  yMax: number;
  xMin: number;
  xMax: number;
}

const TOOLTIP_STYLE = {
  backgroundColor: '#1A2035',
  border: '1px solid #1F2937',
  borderRadius: '2px',
  fontSize: '12px',
  fontFamily: 'JetBrains Mono',
  color: '#E5E7EB',
};

interface TimeSeriesChartProps {
  prepared: PreparedSeries;
  recessionRanges: RecessionRange[];
  color: string;
  unit: string;
  chartType: 'line' | 'bar';
  seriesLabel: string;
  height: number;
  /** When true, render with denser ticks and visible dots for reading specific dates. */
  detailed?: boolean;
  /** Custom value formatter; defaults to formatValue with the given unit. */
  valueFormatter?: (v: number) => string;
  /** Notifies parent of the currently hovered data point (or null when not hovering). */
  onHover?: (point: { dateMs: number; value: number } | null) => void;
}

export function TimeSeriesChart({
  prepared,
  recessionRanges,
  color,
  unit,
  chartType,
  seriesLabel,
  height,
  detailed = false,
  valueFormatter,
  onHover,
}: TimeSeriesChartProps) {
  const { observations, yMin, yMax, xMin, xMax } = prepared;
  const fmtValue = valueFormatter ?? ((v: number) => formatValue(v, unit));

  const visibleRecessions = useMemo(
    () => computeVisibleRecessions(observations, recessionRanges),
    [observations, recessionRanges],
  );

  const recessionAreas = visibleRecessions.map((r, i) => (
    <ReferenceArea
      key={`rec-${i}`}
      x1={r.startMs}
      x2={r.endMs}
      y1={yMin}
      y2={yMax}
      fill="#374151"
      fillOpacity={0.3}
      ifOverflow="extendDomain"
    />
  ));

  const xAxis = (
    <XAxis
      dataKey="dateMs"
      type="number"
      scale="time"
      domain={[xMin, xMax]}
      axisLine={false}
      tickLine={false}
      tick={{ fill: '#6B7280', fontSize: detailed ? 11 : 10, fontFamily: 'JetBrains Mono' }}
      tickFormatter={detailed ? formatDateTickDetailed : formatDateTick}
      minTickGap={detailed ? 60 : 40}
    />
  );

  const yAxis = (
    <YAxis
      axisLine={false}
      tickLine={false}
      tick={{ fill: '#6B7280', fontSize: detailed ? 11 : 10, fontFamily: 'JetBrains Mono' }}
      tickFormatter={(v) => fmtValue(v)}
      domain={['auto', 'auto']}
      width={detailed ? 64 : 55}
      tickCount={detailed ? 8 : undefined}
    />
  );

  const tooltip = (
    <Tooltip
      contentStyle={TOOLTIP_STYLE}
      formatter={(value: number | undefined) => [fmtValue(value ?? 0), seriesLabel]}
      labelFormatter={(l) => formatDateTickDetailed(Number(l))}
    />
  );

  const handleMouseMove = onHover
    ? (state: { activeLabel?: string | number; activePayload?: { value?: number }[] }) => {
        const label = state?.activeLabel;
        const value = state?.activePayload?.[0]?.value;
        if (label != null && value != null) {
          onHover({ dateMs: Number(label), value: Number(value) });
        }
      }
    : undefined;

  const handleMouseLeave = onHover ? () => onHover(null) : undefined;

  return (
    <ResponsiveContainer width="100%" height={height}>
      {chartType === 'line' ? (
        <LineChart
          data={observations}
          margin={{ top: 4, right: 8, bottom: 0, left: 4 }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {recessionAreas}
          {xAxis}
          {yAxis}
          {tooltip}
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={detailed ? 2 : 1.5}
            dot={detailed && observations.length <= 80 ? { r: 1.5, fill: color } : false}
            activeDot={{ r: detailed ? 4 : 3, fill: color }}
            connectNulls
          />
        </LineChart>
      ) : (
        <BarChart
          data={observations}
          margin={{ top: 4, right: 8, bottom: 0, left: 4 }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {recessionAreas}
          {xAxis}
          {yAxis}
          {tooltip}
          <Bar dataKey="value" fill={color} />
        </BarChart>
      )}
    </ResponsiveContainer>
  );
}

export function prepareSeries(
  raw: { date: string; value: number | null }[] | undefined,
): PreparedSeries {
  const obs: ChartObservation[] = (raw ?? []).map((o) => ({ ...o, dateMs: Date.parse(o.date) }));
  let yMin = Infinity;
  let yMax = -Infinity;
  let hasData = false;
  for (const o of obs) {
    if (o.value === null) continue;
    hasData = true;
    if (o.value < yMin) yMin = o.value;
    if (o.value > yMax) yMax = o.value;
  }
  return {
    observations: obs,
    hasData,
    yMin: hasData ? yMin : 0,
    yMax: hasData ? yMax : 1,
    xMin: obs.length ? obs[0].dateMs : 0,
    xMax: obs.length ? obs[obs.length - 1].dateMs : 1,
  };
}

function computeVisibleRecessions(
  observations: ChartObservation[],
  recessionRanges: RecessionRange[],
): { startMs: number; endMs: number }[] {
  if (observations.length === 0) return [];
  const first = observations[0].date;
  const last = observations[observations.length - 1].date;
  return recessionRanges
    .filter((r) => r.end >= first && r.start <= last)
    .map((r) => ({ startMs: Date.parse(r.start), endMs: Date.parse(r.end) }));
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatDateTick(value: number): string {
  const d = new Date(value);
  return `${MONTHS[d.getUTCMonth()]} '${String(d.getUTCFullYear()).slice(2)}`;
}

/** Full month-day-year, used in expanded views and tooltips for precise dates. */
export function formatDateTickDetailed(value: number): string {
  const d = new Date(value);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

export function formatValue(v: number, unit: string): string {
  if (unit === '%') return v.toFixed(2) + '%';
  if (unit === 'K') return v.toFixed(0) + 'K';
  if (unit === '$T') return '$' + (v / 1_000_000).toFixed(2) + 'T';
  return v.toFixed(2);
}

export function deriveRecessionRanges(
  observations: { date: string; value: number | null }[],
): RecessionRange[] {
  const ranges: RecessionRange[] = [];
  let start: string | null = null;
  let prev: string | null = null;
  for (const o of observations) {
    if (o.value === 1) {
      if (start === null) start = o.date;
      prev = o.date;
    } else if (start !== null && prev !== null) {
      ranges.push({ start, end: prev });
      start = null;
      prev = null;
    }
  }
  if (start !== null && prev !== null) ranges.push({ start, end: prev });
  return ranges;
}
